import React, {
  useRef,
  useEffect,
  useState,
  forwardRef,
  useImperativeHandle,
  ReactElement,
} from 'react'
import { getStorage, getBytes, ref as firebaseRef } from 'firebase/storage'
import * as pdfjsLib from 'pdfjs-dist'
import type { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api'
import { DocumentSigningTemplateBlockType } from '../../../../lib/db/db.types'

pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf/pdf.worker.mjs'

export interface BlockDisplay {
  id: string
  type: DocumentSigningTemplateBlockType
  color: string
  label: string
  content: ReactElement
  width?: number
  height?: number
  position: { x: number; y: number }
  page: number
}

interface PdfDisplayProps {
  storageRef: string
  scale: number
  blocks: BlockDisplay[]
  readOnly?: boolean
  selectedBlockId?: string | null
  pendingBlock?: BlockDisplay | null
  onBlockPlaced?: (block: BlockDisplay) => void
  onBlockMoved?: (
    blockId: string,
    newPosition: { x: number; y: number; page: number },
  ) => void
  onSelectBlock?: (blockId: string) => void
  onPageDimensionsChange?: (
    dimensions: Array<{ width: number; height: number }>,
  ) => void
  onBlockResized?: (
    blockId: string,
    newSize: { width: number; height: number },
  ) => void
}

// Add this interface for the ref methods
export interface PdfDisplayHandle {
  scrollToBlock: (blockId: string) => void
}

export const PdfDisplay = forwardRef<PdfDisplayHandle, PdfDisplayProps>(
  (
    {
      storageRef,
      scale,
      blocks,
      selectedBlockId,
      pendingBlock,
      readOnly,
      onBlockPlaced,
      onBlockMoved,
      onSelectBlock,
      onPageDimensionsChange,
      onBlockResized,
    },
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([])
    const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(
      null,
    )
    const [numPages, setNumPages] = useState(0)
    const [isFetching, setIsFetching] = useState(false)

    const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 })
    const [draggedBlockId, setDraggedBlockId] = useState<string | null>(null)
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })
    const [draggedPosition, setDraggedPosition] = useState({ x: 0, y: 0 })

    const [resizingBlockId, setResizingBlockId] = useState<string | null>(null)
    const [resizeDirection, setResizeDirection] = useState<string | null>(null)
    const [initialSize, setInitialSize] = useState({ width: 0, height: 0 })
    const [initialMousePos, setInitialMousePos] = useState({ x: 0, y: 0 })

    const draggedBlock = blocks.find((b) => b.id === draggedBlockId)

    useEffect(() => {
      const fetchPDF = async () => {
        setIsFetching(true)
        try {
          const storage = getStorage()
          const pdfRef = firebaseRef(storage, storageRef)
          const bytes = await getBytes(pdfRef)

          const uint8Array = new Uint8Array(bytes)
          const loadingTask = pdfjsLib.getDocument({ data: uint8Array })
          const pdf = await loadingTask.promise

          setPdfDocument(pdf)
          setNumPages(pdf.numPages)
        } catch (error) {
          console.error('Error fetching PDF:', error)
        } finally {
          setIsFetching(false)
        }
      }

      if (!isFetching && !pdfDocument) {
        fetchPDF()
      }
    }, [storageRef, isFetching, pdfDocument])

    useEffect(() => {
      let isCurrentRender = true
      let currentRenderTask: {
        promise: Promise<void>
        cancel: () => void
      } | null = null

      const collectPageDimensions = async () => {
        if (!pdfDocument || !onPageDimensionsChange) return
        const dimensions = await Promise.all(
          Array.from({ length: numPages }, async (_, i) => {
            const page = await pdfDocument.getPage(i + 1)
            const viewport = page.getViewport({ scale: 1 })
            return {
              width: viewport.width,
              height: viewport.height,
            }
          }),
        )
        onPageDimensionsChange(dimensions)
      }

      const renderPage = async (pageNum: number) => {
        if (!pdfDocument || !isCurrentRender) return

        const canvas = canvasRefs.current[pageNum - 1]
        if (!canvas) return

        try {
          if (currentRenderTask) {
            currentRenderTask.cancel()
            currentRenderTask = null
          }

          const page = await pdfDocument.getPage(pageNum)
          const viewport = page.getViewport({ scale })

          if (
            canvas.width !== viewport.width ||
            canvas.height !== viewport.height
          ) {
            canvas.width = viewport.width
            canvas.height = viewport.height
            canvas.style.width = `${viewport.width}px`
            canvas.style.height = `${viewport.height}px`
          }

          const context = canvas.getContext('2d')
          if (!context) throw new Error('Could not get canvas context')

          context.clearRect(0, 0, canvas.width, canvas.height)

          if (!isCurrentRender) return

          const renderTask = page.render({
            canvasContext: context,
            viewport,
          })

          currentRenderTask = {
            promise: renderTask.promise,
            cancel: () => renderTask.cancel(),
          }

          await renderTask.promise
          return true
        } catch (err) {
          if (isCurrentRender) {
            console.error(`Error rendering page ${pageNum}:`, err)
          }
          return false
        } finally {
          currentRenderTask = null
        }
      }

      const renderPages = async () => {
        await collectPageDimensions()
        for (let pageNum = 1; pageNum <= numPages; pageNum++) {
          if (!isCurrentRender) break
          await renderPage(pageNum)
        }
      }

      renderPages()

      return () => {
        isCurrentRender = false
        if (currentRenderTask) {
          currentRenderTask.cancel()
          currentRenderTask = null
        }
      }
    }, [pdfDocument, numPages, scale, onPageDimensionsChange])

    useEffect(() => {
      if (readOnly || !selectedBlockId || !onBlockMoved) return

      const handleKeyDown = (e: KeyboardEvent) => {
        const selectedBlock = blocks.find((b) => b.id === selectedBlockId)
        if (!selectedBlock) return

        const NUDGE_AMOUNT = 1 // 1px at scale 1
        let deltaX = 0
        let deltaY = 0

        switch (e.key) {
          case 'ArrowLeft':
            deltaX = -NUDGE_AMOUNT
            break
          case 'ArrowRight':
            deltaX = NUDGE_AMOUNT
            break
          case 'ArrowUp':
            deltaY = -NUDGE_AMOUNT
            break
          case 'ArrowDown':
            deltaY = NUDGE_AMOUNT
            break
          default:
            return
        }

        // Prevent scrolling
        e.preventDefault()

        onBlockMoved(selectedBlockId, {
          x: selectedBlock.position.x + deltaX,
          y: selectedBlock.position.y + deltaY,
          page: selectedBlock.page,
        })
      }

      window.addEventListener('keydown', handleKeyDown)
      return () => window.removeEventListener('keydown', handleKeyDown)
    }, [selectedBlockId, blocks, readOnly, onBlockMoved])

    useImperativeHandle(ref, () => ({
      scrollToBlock: (blockId: string) => {
        const block = blocks.find((b) => b.id === blockId)
        if (!block) return

        const canvas = canvasRefs.current[block.page - 1]
        canvas?.scrollIntoView({ behavior: 'smooth' })
      },
    }))

    const handleCanvasClick = (
      e: React.MouseEvent<HTMLCanvasElement>,
      pageNumber: number,
    ) => {
      if (!pendingBlock || readOnly || !onBlockPlaced) return

      const rect = e.currentTarget.getBoundingClientRect()
      const x = (e.clientX - rect.left) / scale
      const y = (e.clientY - rect.top) / scale

      onBlockPlaced({
        ...pendingBlock,
        position: { x, y },
        page: pageNumber,
      })
    }

    const handleContainerMouseMove = (e: React.MouseEvent) => {
      if (resizingBlockId) {
        // Handle resize
        if (!resizeDirection || readOnly) return

        const deltaX = (e.clientX - initialMousePos.x) / scale
        const deltaY = (e.clientY - initialMousePos.y) / scale

        const block = blocks.find((b) => b.id === resizingBlockId)
        if (!block) return

        let newWidth = initialSize.width
        let newHeight = initialSize.height

        switch (resizeDirection) {
          case 'e':
            newWidth = Math.max(50, initialSize.width + deltaX)
            break
          case 's':
            newHeight = Math.max(20, initialSize.height + deltaY)
            break
          case 'se':
            newWidth = Math.max(50, initialSize.width + deltaX)
            newHeight = Math.max(20, initialSize.height + deltaY)
            break
        }

        onBlockResized?.(resizingBlockId, {
          width: newWidth,
          height: newHeight,
        })
      } else if (draggedBlockId && !readOnly) {
        // Handle drag
        setDraggedPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y,
        })
      }
    }

    const handleBlockDragStart = (e: React.MouseEvent, block: BlockDisplay) => {
      if (readOnly) return

      e.stopPropagation()

      // Only start drag if the block is selected
      if (selectedBlockId !== block.id) {
        onSelectBlock?.(block.id)
        return
      }

      setDraggedBlockId(block.id)
      const rect = (e.target as HTMLElement).getBoundingClientRect()
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      })
      setDraggedPosition({
        x: rect.left,
        y: rect.top,
      })
    }

    const handleResizeStart = (
      e: React.MouseEvent,
      block: BlockDisplay,
      direction: string,
    ) => {
      if (readOnly) return
      e.stopPropagation()

      setResizingBlockId(block.id)
      setResizeDirection(direction)
      setInitialMousePos({ x: e.clientX, y: e.clientY })
      setInitialSize({
        width: block.width || 100,
        height: block.height || 30,
      })
    }

    const handleResizeEnd = () => {
      setResizingBlockId(null)
      setResizeDirection(null)
    }

    // TODO - sort out max height / overflow issues- probably need to use javascript
    return (
      <div
        className="relative w-full overflow-y-auto max-h-[calc(100vh-200px)]"
        ref={containerRef}
        onMouseMove={handleContainerMouseMove}
        onMouseUp={(e) => {
          if (readOnly) return

          if (draggedBlockId && onBlockMoved) {
            const block = blocks.find((b) => b.id === draggedBlockId)
            if (!block) return

            const canvas = canvasRefs.current[block.page - 1]
            if (!canvas) return

            const rect = canvas.getBoundingClientRect()
            const x = (e.clientX - rect.left - dragOffset.x) / scale
            const y = (e.clientY - rect.top - dragOffset.y) / scale

            onBlockMoved(draggedBlockId, { x, y, page: block.page })
            setDraggedBlockId(null)
          }
          handleResizeEnd()
        }}
        onMouseLeave={() => {
          if (readOnly) return
          if (draggedBlockId) {
            setDraggedBlockId(null)
          }
          handleResizeEnd()
        }}
      >
        {!pdfDocument ? (
          <p className="text-center">Loading PDF...</p>
        ) : (
          <div className="space-y-4">
            {Array.from({ length: numPages }, (_, pageIndex) => {
              const pageNumber = pageIndex + 1
              return (
                <div key={pageIndex} className="flex justify-center">
                  <div className="border border-gray-300 relative">
                    <canvas
                      ref={(el) => (canvasRefs.current[pageIndex] = el)}
                      onClick={(e) => handleCanvasClick(e, pageNumber)}
                      onMouseMove={(e) => {
                        if (readOnly) return
                        const rect = e.currentTarget.getBoundingClientRect()
                        setMousePosition({
                          x: e.clientX - rect.left,
                          y: e.clientY - rect.top,
                        })
                      }}
                      style={{
                        cursor: pendingBlock ? 'crosshair' : 'default',
                        width: '100%',
                        height: '100%',
                      }}
                    />

                    {pendingBlock && (
                      <div
                        className="absolute pointer-events-none opacity-50"
                        style={{
                          left: `${mousePosition.x}px`,
                          top: `${mousePosition.y}px`,
                          transform: `scale(${scale})`,
                          transformOrigin: 'top left',
                          backgroundColor: pendingBlock.color,
                          borderColor: pendingBlock.color,
                          borderWidth: 1,
                          borderStyle: 'solid',
                        }}
                      >
                        {pendingBlock.content}
                      </div>
                    )}

                    {blocks
                      .filter((b) => b.page === pageNumber)
                      .map((block) => {
                        const isDragging = draggedBlockId === block.id
                        if (isDragging) return null

                        return (
                          <div
                            key={block.id}
                            className={`absolute border select-none ${
                              selectedBlockId === block.id
                                ? 'ring-2 ring-blue-500'
                                : ''
                            } ${!readOnly && selectedBlockId === block.id ? 'cursor-grab' : 'cursor-default'}`}
                            style={{
                              left: `${block.position.x * scale}px`,
                              top: `${block.position.y * scale}px`,
                              width: block.width
                                ? `${block.width * scale}px`
                                : 'auto',
                              height: block.height
                                ? `${block.height * scale}px`
                                : 'auto',
                              zIndex: draggedBlockId === block.id ? 20 : 10,
                              borderColor: block.color,
                              borderWidth: selectedBlockId === block.id ? 2 : 1,
                              borderStyle: 'solid',
                              backgroundColor: block.color + '40',
                            }}
                            onMouseDown={(e) => {
                              if (readOnly) return
                              handleBlockDragStart(e, block)
                            }}
                          >
                            {block.content}

                            {selectedBlockId === block.id && !readOnly && (
                              <>
                                <div
                                  className="absolute w-2 h-full right-0 top-0 cursor-e-resize"
                                  onMouseDown={(e) =>
                                    handleResizeStart(e, block, 'e')
                                  }
                                />
                                <div
                                  className="absolute h-2 w-full bottom-0 left-0 cursor-s-resize"
                                  onMouseDown={(e) =>
                                    handleResizeStart(e, block, 's')
                                  }
                                />
                                <div
                                  className="absolute w-3 h-3 bottom-0 right-0 cursor-se-resize"
                                  onMouseDown={(e) =>
                                    handleResizeStart(e, block, 'se')
                                  }
                                />
                              </>
                            )}
                          </div>
                        )
                      })}
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {draggedBlockId && (
          <div
            className="fixed border select-none pointer-events-none"
            style={{
              left: `${draggedPosition.x}px`,
              top: `${draggedPosition.y}px`,
              width: draggedBlock?.width
                ? `${draggedBlock.width * scale}px`
                : 'auto',
              height: draggedBlock?.height
                ? `${draggedBlock.height * scale}px`
                : 'auto',
              zIndex: 30,
              backgroundColor: (draggedBlock?.color || '') + '40',
              borderColor: draggedBlock?.color || '',
              borderWidth: 1,
              borderStyle: 'solid',
            }}
          >
            {blocks.find((b) => b.id === draggedBlockId)?.content}
          </div>
        )}
      </div>
    )
  },
)
